import { Fragment, useState } from 'react';
import { HiChevronDown, HiViewList, HiSelector } from 'react-icons/hi';
import { formatMoney, formatNumber, categoryColor } from '../../utils/reportFormat';

const SORTS = [
  { key: 'revenue', label: 'الإيرادات' },
  { key: 'profit', label: 'الربح' },
  { key: 'unitsSold', label: 'الكمية' },
  { key: 'margin', label: 'الهامش' },
];

function Profit({ value, margin }) {
  return (
    <div className="flex flex-col items-start">
      <span className={`text-sm font-bold ${value < 0 ? 'text-red-600' : 'text-emerald-600'}`} dir="ltr">
        {formatMoney(value)}
      </span>
      {margin != null && <span className="text-[11px] text-gray-400">{margin}%</span>}
    </div>
  );
}

export default function CategoryTable({ categories, summary }) {
  const [expanded, setExpanded] = useState(() => new Set());
  const [sortKey, setSortKey] = useState('revenue');

  // Colours follow revenue rank so they match the donut regardless of sort.
  const colorById = Object.fromEntries(categories.map((c, i) => [c.id, categoryColor(i)]));
  const rows = [...categories].sort((a, b) => b[sortKey] - a[sortKey]);

  const toggle = (id) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const allOpen = expanded.size === categories.length && categories.length > 0;

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-primary-100/40 overflow-hidden animate-fadeIn">
      <div className="flex flex-wrap items-center gap-3 p-5 border-b border-gray-100">
        <div className="p-2.5 rounded-xl bg-primary-50 text-primary-600">
          <HiViewList className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-base font-bold text-gray-800">تفاصيل المبيعات حسب التصنيف</h3>
          <p className="text-xs text-gray-400">اضغط على أي تصنيف لعرض منتجاته</p>
        </div>
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-1.5 text-xs text-gray-500">
            <HiSelector className="w-4 h-4" />
            <select
              value={sortKey}
              onChange={(e) => setSortKey(e.target.value)}
              className="bg-gray-50 border border-gray-200 rounded-lg px-2 py-1.5 text-xs font-semibold text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500/20"
              aria-label="ترتيب حسب"
            >
              {SORTS.map((s) => <option key={s.key} value={s.key}>ترتيب: {s.label}</option>)}
            </select>
          </label>
          <button
            type="button"
            onClick={() => setExpanded(allOpen ? new Set() : new Set(categories.map((c) => c.id)))}
            className="px-3 py-1.5 rounded-lg text-xs font-semibold text-primary-600 hover:bg-primary-50 transition-all"
          >
            {allOpen ? 'طي الكل' : 'توسيع الكل'}
          </button>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full min-w-[760px]">
          <thead>
            <tr className="bg-gray-50/80 text-right text-xs font-semibold text-gray-400 tracking-wider">
              <th className="px-5 py-3">التصنيف</th>
              <th className="px-4 py-3">العمليات</th>
              <th className="px-4 py-3">الكمية</th>
              <th className="px-4 py-3">الإيرادات</th>
              <th className="px-4 py-3">التكلفة*</th>
              <th className="px-4 py-3">الربح*</th>
              <th className="px-4 py-3 w-40">الحصة</th>
              <th className="px-3 py-3 w-10" aria-label="توسيع" />
            </tr>
          </thead>
          <tbody>
            {rows.map((c) => {
              const open = expanded.has(c.id);
              const color = colorById[c.id];
              return (
                <Fragment key={c.id}>
                  <tr
                    onClick={() => toggle(c.id)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        toggle(c.id);
                      }
                    }}
                    tabIndex={0}
                    aria-expanded={open}
                    className={`cursor-pointer border-t border-gray-50 transition-colors focus:outline-none focus-visible:bg-primary-50/50 ${
                      open ? 'bg-primary-50/40' : 'hover:bg-primary-50/25'
                    }`}
                  >
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-3">
                        <span className="w-1.5 h-9 rounded-full" style={{ background: color }} />
                        <div>
                          <p className="text-sm font-bold text-gray-800">{c.name}</p>
                          <p className="text-[11px] text-gray-400">{c.products.length} منتج</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-4 text-sm font-semibold text-gray-700">{formatNumber(c.salesCount)}</td>
                    <td className="px-4 py-4 text-sm font-semibold text-gray-700">{formatNumber(c.unitsSold)}</td>
                    <td className="px-4 py-4 text-sm font-bold text-gray-800" dir="ltr" style={{ textAlign: 'right' }}>
                      {formatMoney(c.revenue)}
                    </td>
                    <td className="px-4 py-4 text-sm text-gray-500" dir="ltr" style={{ textAlign: 'right' }}>
                      {formatMoney(c.cost)}
                    </td>
                    <td className="px-4 py-4"><Profit value={c.profit} margin={c.margin} /></td>
                    <td className="px-4 py-4">
                      <div className="flex items-center gap-2">
                        <div className="flex-1 bg-gray-100 rounded-full h-1.5 overflow-hidden">
                          <div className="h-1.5 rounded-full transition-all duration-700" style={{ width: `${c.share}%`, background: color }} />
                        </div>
                        <span className="text-xs font-semibold text-gray-500 w-11 text-left">{c.share}%</span>
                      </div>
                    </td>
                    <td className="px-3 py-4">
                      <HiChevronDown className={`w-5 h-5 text-gray-400 transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
                    </td>
                  </tr>

                  {open && (
                    <tr className="bg-gray-50/60">
                      <td colSpan={8} className="px-5 pb-4 pt-1">
                        <div className="rounded-xl border border-primary-100/50 bg-white overflow-hidden animate-slideDown">
                          <table className="w-full">
                            <thead>
                              <tr className="text-right text-[11px] font-semibold text-gray-400 border-b border-gray-100">
                                <th className="px-4 py-2 w-10">#</th>
                                <th className="px-4 py-2">المنتج</th>
                                <th className="px-4 py-2">الكمية</th>
                                <th className="px-4 py-2">متوسط السعر</th>
                                <th className="px-4 py-2">الإيرادات</th>
                                <th className="px-4 py-2">الربح*</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-50">
                              {c.products.map((p, i) => (
                                <tr key={p.id} className="hover:bg-primary-50/20">
                                  <td className="px-4 py-2.5 text-xs font-bold text-gray-300">{i + 1}</td>
                                  <td className="px-4 py-2.5">
                                    <p className="text-sm font-semibold text-gray-700">{p.name}</p>
                                    {p.barcode && <p className="text-[11px] text-gray-400 font-mono">{p.barcode}</p>}
                                  </td>
                                  <td className="px-4 py-2.5 text-sm font-semibold text-gray-700">{formatNumber(p.unitsSold)}</td>
                                  <td className="px-4 py-2.5 text-sm text-gray-500" dir="ltr" style={{ textAlign: 'right' }}>
                                    {formatMoney(p.avgUnitPrice)}
                                  </td>
                                  <td className="px-4 py-2.5 text-sm font-bold text-gray-800" dir="ltr" style={{ textAlign: 'right' }}>
                                    {formatMoney(p.revenue)}
                                  </td>
                                  <td className="px-4 py-2.5"><Profit value={p.profit} /></td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
          <tfoot>
            <tr className="border-t-2 border-primary-100 bg-primary-50/40">
              <td className="px-5 py-4 text-sm font-bold text-primary-700">الإجمالي</td>
              <td className="px-4 py-4 text-sm font-bold text-gray-800">{formatNumber(summary.salesCount)}</td>
              <td className="px-4 py-4 text-sm font-bold text-gray-800">{formatNumber(summary.unitsSold)}</td>
              <td className="px-4 py-4 text-sm font-bold text-primary-700" dir="ltr" style={{ textAlign: 'right' }}>
                {formatMoney(summary.revenue)}
              </td>
              <td className="px-4 py-4 text-sm font-semibold text-gray-600" dir="ltr" style={{ textAlign: 'right' }}>
                {formatMoney(summary.cost)}
              </td>
              <td className="px-4 py-4"><Profit value={summary.profit} margin={summary.margin} /></td>
              <td className="px-4 py-4 text-xs font-semibold text-gray-500">100%</td>
              <td />
            </tr>
          </tfoot>
        </table>
      </div>

      <p className="px-5 py-3 text-[11px] text-gray-400 border-t border-gray-50">
        * التكلفة والربح تقديريان، محسوبان وفق سعر الشراء الحالي لكل منتج. «العمليات» في الإجمالي تحسب كل فاتورة مرة واحدة حتى لو شملت عدة تصنيفات.
      </p>
    </div>
  );
}
